/**
 * Order History Component - User Profile
 * Shows user's order history with details
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  MapPin,
  Calendar,
  Star,
  Loader2,
  Download,
  RefreshCw,
  AlertCircle,
  Wallet,
  HelpCircle,
  Info,
} from 'lucide-react';
import orderService from '../../services/orderService';
import ReviewModal from './ReviewModal';
import CancelOrderModal from './CancelOrderModal';
import { OrderStatus } from '../../lib/constants';
import OrderAmendmentApproval from './OrderAmendmentApproval';
import { formatPaymentMethod, PAYMENT_METHODS } from '../../lib/paymentLabels';


// Payment wording is shared with the admin screens — see lib/paymentLabels.

const OrderHistory = ({ onReviewSubmitted }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelModal, setCancelModal] = useState({ open: false, order: null });
  const [refundedToWalletMap, setRefundedToWalletMap] = useState({});
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [reviewOrderId, setReviewOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const ORDERS_PER_PAGE = 10;

  useEffect(() => {
    setCurrentPage(1);
    fetchOrders(1);
  }, [statusFilter]);

  useEffect(() => {
    fetchOrders(currentPage);
  }, [currentPage]);

  const fetchOrders = async (page = currentPage) => {
    setLoading(true);
    const result = await orderService.getMyOrders({
      status: statusFilter,
      page,
      limit: ORDERS_PER_PAGE,
    });
    if (result.success) {
      setOrders(result.data.orders);
      setTotalPages(result.data.pagination.pages);
      setTotalOrders(result.data.pagination.total);
    }
    setLoading(false);
  };

  const handleDownloadInvoice = async (orderId) => {
    setDownloadingInvoice(orderId);
    const result = await orderService.downloadInvoice(orderId);
    if (!result.success) {
      alert(result.error || 'Failed to download invoice');
    }
    setDownloadingInvoice(null);
  };

  const openCancelModal = (order) => setCancelModal({ open: true, order });
  const closeCancelModal = () => { if (!cancellingId) setCancelModal({ open: false, order: null }); };

  const handleCancelConfirm = async (refundTo, reason) => {
    const { order } = cancelModal;
    setCancellingId(order.id);
    const result = await orderService.cancelOrder(order.id, reason || '', refundTo ?? undefined);
    setCancellingId(null);
    if (result.success) {
      const walletRefunded =
        refundTo === 'wallet' ||
        order.paymentMethod === PAYMENT_METHODS.WALLET ||
        (result.data?.refund?.to === 'wallet');
      setRefundedToWalletMap(prev => ({ ...prev, [order.id]: walletRefunded }));
      setOrders(prev =>
        prev.map(o => o.id === order.id
          ? {
              ...o,
              status: 'cancelled',
              cancelReason: result.data?.cancelReason || reason || o.cancelReason,
              paymentStatus: result.data?.paymentStatus ?? o.paymentStatus
            }
          : o)
      );
      setCancelModal({ open: false, order: null });
    } else {
      alert(result.error || 'Failed to cancel order');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'processing': return <Package className="w-4 h-4" />;
      case 'shipped': return <Truck className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'processing': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'shipped': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'delivered': return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getProgressSteps = (status) => {
    const steps = ['pending', 'processing', 'shipped', 'delivered'];
    const normalizedStatus = status?.toLowerCase() || 'pending';
    const currentIndex = steps.indexOf(normalizedStatus);
    // If status is 'confirmed', treat it as 'pending' for display purposes
    const effectiveIndex = currentIndex === -1 ? 0 : currentIndex;
    return steps.map((step, idx) => ({
      step,
      completed: idx <= effectiveIndex && normalizedStatus !== 'cancelled',
      current: idx === effectiveIndex
    }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-100 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Order History</h2>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
        >
          <option value="">All Orders</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">No orders found</p>
          <p className="text-sm text-gray-400">Your order history will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div 
              key={order.id} 
              className="border border-gray-200 rounded-xl overflow-hidden"
            >
              {/* Order Header */}
              <div 
                className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
              >
                {/* Top row: Order ID, Date, Status */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-semibold text-gray-800 bg-white px-2 py-1 rounded border border-gray-200">
                      {order.orderNumber || `#${order.id}`}
                    </span>
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(order.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      <span className="capitalize">{order.status}</span>
                    </span>
                    {expandedOrder === order.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {/* Bottom row: Items summary and Total */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Package className="w-4 h-4 text-gray-400" />
                    <span>
                      {order.items.reduce((sum, item) => sum + item.quantity, 0)} item{order.items.reduce((sum, item) => sum + item.quantity, 0) > 1 ? 's' : ''}
                      {order.items.length <= 2 && (
                        <span className="text-gray-400 ml-1">
                          ({order.items.map(i => i.name).join(', ')})
                        </span>
                      )}
                    </span>
                  </div>
                  <span className="text-lg font-semibold text-gray-800">{formatCurrency(order.total)}</span>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedOrder === order.id && (
                <div className="p-4 border-t border-gray-200">
                  {/* Amendment approval request (BMF-BRD-2026-014) */}
                  {order.amendmentStatus === 'PENDING_CUSTOMER_APPROVAL' && (
                    <OrderAmendmentApproval
                      order={order}
                      onDecided={() => fetchOrders(currentPage)}
                    />
                  )}

                  {/* Progress Bar */}
                  {order.status !== 'cancelled' && (
                    <div className="mb-6">
                      <div className="relative flex items-center justify-between">
                        {/* Full background line */}
                        <div className="absolute inset-x-0 top-4 h-0.5 bg-gray-200 z-0" />
                        {/* Green progress line */}
                        {(() => {
                          const steps = ['pending', 'processing', 'shipped', 'delivered'];
                          const normalizedStatus = order.status?.toLowerCase() || 'pending';
                          let idx = steps.indexOf(normalizedStatus);
                          if (idx === -1) idx = 0; // Default to pending for unknown statuses
                          // Show green line up to current step (use idx+0.5 to reach center of current circle)
                          const pct = ((idx + 0.5) / (steps.length)) * 100;
                          return <div className="absolute left-0 top-4 h-0.5 bg-green-500 z-0 rounded" style={{ width: `${pct}%` }} />;
                        })()}
                        {getProgressSteps(order.status).map((step) => (
                          <div key={step.step} className="relative z-10 flex flex-col items-center flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                              step.completed ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 text-gray-400'
                            }`}>
                              {step.step === 'pending'    && <Clock className="w-4 h-4" />}
                              {step.step === 'processing' && <Package className="w-4 h-4" />}
                              {step.step === 'shipped'    && <Truck className="w-4 h-4" />}
                              {step.step === 'delivered'  && <CheckCircle className="w-4 h-4" />}
                            </div>
                            <span className={`text-xs mt-1 capitalize ${step.current ? 'text-green-600 font-semibold' : step.completed ? 'text-gray-600' : 'text-gray-400'}`}>
                              {step.step}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Delivery Type / Date */}
                  <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                    <Calendar className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-orange-500 font-medium uppercase tracking-wide">
                        Delivery Type
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {order.deliveryType === 'scheduled' || order.deliveryDate ? (
                          <>
                            Scheduled Delivery
                            {order.deliveryDate && (
                              <span className="text-gray-600 font-normal">
                                {' · '}
                                {new Date(order.deliveryDate).toLocaleDateString('en-IN', {
                                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata'
                                })}
                              </span>
                            )}
                            {order.deliverySlot && order.deliverySlot !== 'On-Demand' && (
                              <span className="text-gray-500 font-normal"> · {order.deliverySlot}</span>
                            )}
                          </>
                        ) : (
                          <span>⚡ On-Demand Delivery</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-3">Items</h4>
                    <div className="space-y-2">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-[#f2f8eb] flex items-center justify-center">
                              <Package className="w-6 h-6 text-[#548B2E]" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{item.name}</p>
                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                          </div>
                          <p className="font-medium text-gray-800">{formatCurrency(item.price * item.quantity)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg mb-4">
                    <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-800">{order.shippingAddress.name}</p>
                      <p className="text-sm text-gray-600">
                        {order.shippingAddress.address}, {order.shippingAddress.city}
                      </p>
                      <p className="text-sm text-gray-600">
                        {order.shippingAddress.state} - {order.shippingAddress.pincode}
                      </p>
                      <p className="text-sm text-gray-500">{order.shippingAddress.phone}</p>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="border-t border-gray-200 pt-4 space-y-1">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal</span>
                      <span>{formatCurrency(order.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Shipping</span>
                      <span>{order.shipping === 0 ? 'Free' : formatCurrency(order.shipping)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Tax</span>
                      <span>{formatCurrency(order.tax)}</span>
                    </div>
                    <div className="flex justify-between font-medium text-gray-800 pt-2 border-t border-gray-200">
                      <span>Total</span>
                      <span>{formatCurrency(order.total)}</span>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Payment Method</p>
                        <p className="text-sm font-medium text-gray-800">
                          {formatPaymentMethod(order)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                          order.paymentStatus === 'refunded' ? 'bg-blue-100 text-blue-700' :
                          order.paymentStatus === 'partial_refund' ? 'bg-amber-100 text-amber-700' :
                          order.paymentStatus === 'failed' ? 'bg-red-100 text-red-700' :
                          order.paymentStatus === 'refund_requested' ? 'bg-amber-100 text-amber-700' :
                          order.paymentStatus === 'refund_rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {order.paymentStatus === 'refund_requested' ? 'Refund Pending Approval' :
                           order.paymentStatus === 'refund_rejected' ? 'Refund Declined' :
                           order.paymentStatus === 'partial_refund' ? 'Partial Refund' :
                           order.paymentStatus}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Order Note */}
                  {order.orderNote && (
                    <div className="mt-4 p-3 bg-amber-50/60 border border-amber-200/80 rounded-lg">
                      <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Order Note</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.orderNote}</p>
                    </div>
                  )}

                  {/* Cancellation Reason / Notice */}
                  {order.status === 'cancelled' && (
                    <div className="mt-4 flex items-start gap-3 p-4 bg-red-50/70 border border-red-200 rounded-xl">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">Order Cancelled</p>
                        <p className="text-xs text-red-600 mt-0.5">
                          {order.cancelReason && order.cancelReason.trim() !== ''
                            ? `Reason: "${order.cancelReason}"`
                            : 'This order has been cancelled'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Refund Status — shown for all cancelled paid non-COD orders */}
                  {order.status === 'cancelled' && order.paymentMethod !== PAYMENT_METHODS.COD && (() => {
                    const toWallet = refundedToWalletMap[order.id] ?? (order.paymentMethod === PAYMENT_METHODS.WALLET);
                    // 'partial_refund' reads as fully refunded here, because this
                    // whole block only renders for a CANCELLED order — and a
                    // cancelled order has had everything still held returned.
                    // Orders amended before BMF-159 are stuck on that status
                    // (approveRefundRequest wrote it without checking whether the
                    // order was cancelled), and they were falling through every
                    // arm below to `null`: a cancelled, refunded order showing
                    // the customer no refund message whatsoever.
                    if (order.paymentStatus === 'refunded' || order.paymentStatus === 'partial_refund') {
                      return toWallet ? (
                        <div className="mt-4 flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                          <Wallet className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-green-800">Refunded to BMF Coins</p>
                            <p className="text-xs text-green-600 mt-0.5">
                              {formatCurrency(order.total)} has been credited to your BMF Coins balance instantly.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                          <RefreshCw className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-blue-800">Refund Initiated</p>
                            <p className="text-xs text-blue-600 mt-0.5">
                              Your refund of {formatCurrency(order.total)} has been initiated and will be credited to your original payment method within 5–7 business days.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    if (order.paymentStatus === 'failed') {
                      return (
                        <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-red-800">Refund Could Not Be Processed</p>
                            <p className="text-xs text-red-600 mt-0.5">
                              We were unable to automatically process your refund. Our team has been notified and will reach out to you within 24 hours.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    if (order.paymentStatus === 'refund_requested') {
                      return (
                        <div className="mt-4 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-amber-800">Awaiting Admin Approval</p>
                            <p className="text-xs text-amber-600 mt-0.5">
                              Your refund request of {formatCurrency(order.total)} is under review. Once approved, it will be credited to your original payment method within 5–7 business days.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    if (order.paymentStatus === 'refund_rejected') {
                      return (
                        <div className="mt-4 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-red-800">Refund Declined</p>
                            <p className="text-xs text-red-600 mt-0.5">
                              Your refund request was not approved. Please contact support if you believe this is an error.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    if (order.paymentStatus === 'paid') {
                      return (
                        <div className="mt-4 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <Clock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-amber-800">Refund Pending</p>
                            <p className="text-xs text-amber-600 mt-0.5">
                              Your refund of {formatCurrency(order.total)} is being processed. Please allow up to 24 hours for it to be initiated.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Tracking */}
                  {order.trackingNumber && (
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                      <p className="text-sm text-purple-700">
                        <span className="font-medium">Tracking:</span> {order.trackingNumber}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-4 flex flex-wrap gap-3">
                    {order.status === OrderStatus.DELIVERED && (
                      <button
                        onClick={() => handleDownloadInvoice(order.id)}
                        disabled={downloadingInvoice === order.id}
                        className="flex items-center gap-2 px-4 py-2 border border-[#548B2E] text-[#548B2E] rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50 text-sm font-medium"
                      >
                        {downloadingInvoice === order.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Download Invoice
                      </button>
                    )}
                    {['pending', 'confirmed', 'processing'].includes(order.status) && (
                      <button
                        onClick={() => openCancelModal(order)}
                        disabled={cancellingId === order.id}
                        className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 text-sm font-medium"
                      >
                        {cancellingId === order.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        Cancel Order
                      </button>
                    )}
                    {order.status === 'delivered' && (
                      <button
                        onClick={() => setReviewOrderId(order.id)}
                        className="flex items-center gap-2 px-4 py-2 border border-yellow-400 text-yellow-700 rounded-lg hover:bg-yellow-50 transition-colors text-sm font-medium"
                      >
                        <Star className="w-4 h-4" />
                        Write a Review
                      </button>
                    )}
                    {(() => {
                      if (!order.deliveredAt) {
                        return (
                          <Link
                            to={`/profile?tab=complaints&orderReference=${encodeURIComponent(order.orderNumber || order.id)}`}
                            className="flex items-center gap-2 px-4 py-2 border border-orange-400 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium"
                          >
                            <HelpCircle className="w-4 h-4" />
                            Raise Issue
                          </Link>
                        );
                      }
                      const deliveryTime = new Date(order.deliveredAt).getTime();
                      const isWithin24Hours = (Date.now() - deliveryTime) <= 24 * 60 * 60 * 1000;
                      if (isWithin24Hours) {
                        return (
                          <Link
                            to={`/profile?tab=complaints&orderReference=${encodeURIComponent(order.orderNumber || order.id)}`}
                            className="flex items-center gap-2 px-4 py-2 border border-orange-400 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium"
                          >
                            <HelpCircle className="w-4 h-4" />
                            Raise Issue
                          </Link>
                        );
                      }
                      return (
                        <div className="w-full mt-2 flex items-center gap-2 text-xs text-[#92400E] bg-[#FFFBEB] p-2.5 rounded-lg border border-[#FDE68A]">
                          <Info className="w-4 h-4 text-[#D97706] flex-shrink-0" />
                          <span>As per our company policy, any product quality issues, damaged products, missing items, or incorrect deliveries must be reported within 24 hours of receiving the order.</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Showing {((currentPage - 1) * ORDERS_PER_PAGE) + 1}–{Math.min(currentPage * ORDERS_PER_PAGE, totalOrders)} of {totalOrders} orders
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span key={`e-${idx}`} className="px-2 text-gray-400">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                      currentPage === p
                        ? 'bg-[#548B2E] text-white border-[#548B2E]'
                        : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewOrderId && (
        <ReviewModal
          orderId={reviewOrderId}
          onClose={() => setReviewOrderId(null)}
          onSuccess={() => { setReviewOrderId(null); onReviewSubmitted?.(); }}
        />
      )}

      {/* Cancel Order Modal */}
      {cancelModal.open && (
        <CancelOrderModal
          order={cancelModal.order}
          onConfirm={handleCancelConfirm}
          onClose={closeCancelModal}
          isLoading={cancellingId === cancelModal.order?.id}
        />
      )}
    </div>
  );
};

export default OrderHistory;
